import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { Money } from "@/components/common/Money";
import { toast } from "sonner";
import { ArrowLeft, Download } from "lucide-react";

interface Invoice {
  invoiceNo: string;
  issuedAt: string;
  subtotal: number;
  tax: number;
  fees: number;
  total: number;
  currency: string;
  lineItems: Array<{
    garmentType: string;
    alterationType: string;
    unitPrice: number;
    qty: number;
    total: number;
    tagCode?: string;
  }>;
  customer: {
    name: string;
    email?: string;
    phone?: string;
  };
  order: {
    publicId: string;
  };
  payment: {
    paidAt: string;
  };
}

export default function InvoicePage() {
  const navigate = useNavigate();
  const params = useParams();
  const orderId = params.id as string;

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState<Invoice | null>(null);

  useEffect(() => {
    fetchInvoice();
  }, [orderId]);

  async function fetchInvoice() {
    try {
      const response = await fetch(`/api/customer/orders/${orderId}/invoice`);
      if (!response.ok) {
        if (response.status === 404) {
          toast.error("Invoice not found for this order");
          navigate(`/customer/orders/${orderId}`);
          return;
        }
        throw new Error("Failed to fetch invoice");
      }

      const data = await response.json();
      setInvoice(data.invoice);
    } catch (error) {
      toast.error("Failed to load invoice");
      navigate(`/customer/orders/${orderId}`);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Invoice" />
        <Skeleton className="h-[600px]" />
      </div>
    );
  }

  if (!invoice) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Invoice ${invoice.invoiceNo}`}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.print()}>
              <Download className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button variant="outline" onClick={() => navigate(`/customer/orders/${orderId}`)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Order
            </Button>
          </div>
        }
      />

      <div className="max-w-4xl mx-auto">
        <Card className="print:shadow-none">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-2xl">Invoice</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">{invoice.invoiceNo}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Issue Date</p>
                <p className="font-medium">{new Date(invoice.issuedAt).toLocaleDateString()}</p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Bill To</p>
                <p className="font-medium">{invoice.customer.name}</p>
                {invoice.customer.email && (
                  <p className="text-sm text-muted-foreground">{invoice.customer.email}</p>
                )}
                {invoice.customer.phone && (
                  <p className="text-sm text-muted-foreground">{invoice.customer.phone}</p>
                )}
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Order Details</p>
                <p className="font-medium">Order: {invoice.order.publicId}</p>
                <p className="text-sm text-muted-foreground">
                  Paid: {new Date(invoice.payment.paidAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            <Separator />

            <div>
              <p className="text-sm font-medium mb-4">Items</p>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium">Description</th>
                      <th className="text-right p-3 text-sm font-medium">Qty</th>
                      <th className="text-right p-3 text-sm font-medium">Unit Price</th>
                      <th className="text-right p-3 text-sm font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.lineItems.map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="p-3">
                          <p className="font-medium">{item.garmentType}</p>
                          <p className="text-sm text-muted-foreground">{item.alterationType}</p>
                          {item.tagCode && (
                            <p className="text-xs text-muted-foreground">Tag: {item.tagCode}</p>
                          )}
                        </td>
                        <td className="p-3 text-right">{item.qty}</td>
                        <td className="p-3 text-right">
                          <Money amount={item.unitPrice} currency={invoice.currency} />
                        </td>
                        <td className="p-3 text-right font-medium">
                          <Money amount={item.total} currency={invoice.currency} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end">
              <div className="w-full max-w-xs space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <Money amount={invoice.subtotal} currency={invoice.currency} />
                </div>
                {invoice.tax > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax</span>
                    <Money amount={invoice.tax} currency={invoice.currency} />
                  </div>
                )}
                {invoice.fees > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Delivery Fee</span>
                    <Money amount={invoice.fees} currency={invoice.currency} />
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-semibold text-lg">
                  <span>Total</span>
                  <Money amount={invoice.total} currency={invoice.currency} />
                </div>
              </div>
            </div>

            <div className="pt-6 text-center text-sm text-muted-foreground border-t">
              Thank you for your business!
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
